import type { components } from "@/types/api";

/** sessionStorage key for the post-payment finalize payload (colon-namespaced per repo convention). */
export const PENDING_FINALIZE_KEY = "checkout:pending-finalize";

/**
 * Finalize payload handed from the checkout flow (after the irreversible on-chain
 * buyNft) to the /checkout/success route that owns POST /order.
 *
 * `order` is the exact CreateOrderDto the success route replays; `itemCount`
 * drives the success copy. Must be fully JSON-safe: build `order.user_id` as a
 * string before persisting (the on-chain user id is a bigint, not serializable).
 */
export interface PendingFinalize {
  order: components["schemas"]["CreateOrderDto"];
  itemCount: number;
}

/**
 * In-memory fallback for the finalize payload.
 *
 * The redirect to /checkout/success is a CLIENT-SIDE navigation (router.push) —
 * Next.js App Router does NOT reload JS modules, so this module-level value
 * survives the hop within the same tab even when sessionStorage is unavailable
 * (private mode / disabled storage). It does NOT survive a full reload —
 * sessionStorage covers that case. The two are complementary.
 *
 * Safe against SSR cross-request leakage because persist is only ever called
 * client-side (in the hook's onSuccess, after the on-chain tx); the server
 * never writes this.
 */
let memoryFinalize: PendingFinalize | null = null;

/**
 * Persist the finalize payload to sessionStorage AND an in-memory fallback.
 * Without the in-memory fallback a silent sessionStorage write failure would
 * strand the paid order: the success route would find nothing, show the neutral
 * "processing" message, and never fire POST /order.
 */
export function persistPendingFinalize(payload: PendingFinalize): void {
  memoryFinalize = payload;
  if (typeof window === "undefined") return;
  try {
    sessionStorage.setItem(PENDING_FINALIZE_KEY, JSON.stringify(payload));
  } catch {
    // sessionStorage unavailable — the in-memory fallback still carries the
    // payload across the client-side redirect so the success route can finalize.
  }
}

/** Read the finalize payload from sessionStorage, falling back to memory; null if absent/corrupt. */
export function readPendingFinalize(): PendingFinalize | null {
  if (typeof window !== "undefined") {
    try {
      const raw = sessionStorage.getItem(PENDING_FINALIZE_KEY);
      if (raw) return JSON.parse(raw) as PendingFinalize;
    } catch {
      // corrupt / unavailable — fall through to the in-memory fallback
    }
  }
  return memoryFinalize;
}

/** Remove the finalize payload from both channels (call after a successful finalize). */
export function clearPendingFinalize(): void {
  memoryFinalize = null;
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(PENDING_FINALIZE_KEY);
  } catch {
    // ignore
  }
}
